import { isMasurenPortal } from '~/utils/isMasurenPortal'
import {
  MASUREN_DEFERRED_TRANSLATION_DOMAINS,
  masurenPathNeedsTranslationDomain
} from '~/utils/masurenTranslationDomains'

export type TranslationMap = Record<string, Record<string, string>>

const useTranslations = (domain: string) => {
  const defaultDomain = domain

  const translations = useState<TranslationMap|null>('i18n-translations', () => null)
  const usedKeys = useState<Record<string, Record<string, true>>>('i18n-used-keys', () => ({}))
  const translationsAreComplete = useState<boolean>('i18n-complete', () => false)

  const loadTranslations = async () => {
    if (translations.value !== null) {
      return
    }

    const nuxtApp = useNuxtApp()
    const { $backend } = nuxtApp
    const { currentPortal } = usePortals()
    const route = useRoute()
    const { data: translationsData } = await $backend.translations.getAllTranslations()
    const grouped = translationsData.value?.data ?? null

    if (grouped && isMasurenPortal(currentPortal.value?.portal?.portal)) {
      const trimmed = Object.fromEntries(
        Object.entries(grouped).filter(([domain]) =>
          !MASUREN_DEFERRED_TRANSLATION_DOMAINS.includes(domain as typeof MASUREN_DEFERRED_TRANSLATION_DOMAINS[number]) ||
          masurenPathNeedsTranslationDomain(route.path, domain)
        )
      )
      translations.value = trimmed
      if (import.meta.server) {
        nuxtApp.runWithContext(() => {
          clearNuxtData('api/translations')
        })
      }
      return
    }

    translations.value = grouped
    translationsAreComplete.value = true
  }

  const ensureDeferredTranslations = async (path: string) => {
    const { currentPortal } = usePortals()
    const { $backend } = useNuxtApp()
    if (!isMasurenPortal(currentPortal.value?.portal?.portal)) {
      return
    }
    if (translations.value === null) {
      await loadTranslations()
    }
    const missing = MASUREN_DEFERRED_TRANSLATION_DOMAINS.filter(deferredDomain =>
      masurenPathNeedsTranslationDomain(path, deferredDomain) &&
      !translations.value?.[deferredDomain]
    )
    if (missing.length === 0) {
      return
    }
    const response = await $backend.translations.fetchAllTranslations()
    const grouped = (response?.data ?? {}) as Record<string, Record<string, string>>
    translations.value = {
      ...(translations.value ?? {}),
      ...Object.fromEntries(missing.filter(deferredDomain => grouped[deferredDomain]).map(deferredDomain => [deferredDomain, grouped[deferredDomain]]))
    }
  }

  const ensureFullTranslations = async () => {
    const { currentPortal } = usePortals()
    const { $backend } = useNuxtApp()
    if (!isMasurenPortal(currentPortal.value?.portal?.portal) || translationsAreComplete.value) {
      return
    }
    if (translations.value === null) {
      await loadTranslations()
    }
    if (translationsAreComplete.value) {
      return
    }
    const response = await $backend.translations.fetchAllTranslations()
    const grouped = (response?.data ?? {}) as TranslationMap
    translations.value = {
      ...grouped,
      ...(translations.value ?? {})
    }
    translationsAreComplete.value = true
  }

  const translate = computed(() =>
    (...args: any[]) => {
      let key, optionalDomain, variables

      if (typeof args[1] === 'string') {
        [key, optionalDomain = null, variables = null] = args
      } else {
        [key, variables = null] = args
      }

      domain = optionalDomain || defaultDomain

      if (import.meta.server && domain && key) {
        if (!usedKeys.value[domain]) {
          usedKeys.value[domain] = {}
        }
        usedKeys.value[domain][key] = true
      }

      let text = translations.value?.[domain]?.[key]

      if (import.meta.client && translations.value && !text) {
        console.warn(`Missing language placeholder:\n${domain} -> ${key}\nhttps://seoadmin.localhost/admin/i18n/domains?domain=${domain}&lang=de`) // eslint-disable-line no-console
      }

      if (text && variables) {
        text = text.replace(/\${(\w+)}/g, (match, name) => {
          return variables[name] ?? match
        })
      }

      return text
    }
  )

  const numerus = nr => parseInt(nr) === 1 ? 'singular' : 'plural'

  return {
    loadTranslations,
    ensureDeferredTranslations,
    ensureFullTranslations,
    translate,
    numerus
  }
}

export default useTranslations
